// OMSEVEN-10852: inside the WinApp (WebView2) only the WinApp refreshes. The page signs in from
// `webview-token` in its URL; after each WinApp refresh the WinApp posts
// { message: 'WEBVIEW_ACCESS_TOKEN', data: { accessToken, accessTokenExpiration } }.
// The token goes where requests (axios-helper), SignalR and logout read it.
import { LOCALSTORAGE_AUTH, LOCALSTORAGE_AUTH2, LOCALSTORAGE_USERSSION } from "../../common/constants";

export const WEBVIEW_ACCESS_TOKEN = "WEBVIEW_ACCESS_TOKEN";

// The part of window.chrome.webview this module uses.
export interface WebView2Host {
    addEventListener(type: "message", listener: (event: { data: unknown }) => void): void;
}

type KeyValueStorage = Pick<Storage, "getItem" | "setItem">;

interface PushedToken {
    accessToken: string;
    accessTokenExpiration: string | null;
}

// Only the fields this module writes; the rest of each stored object is kept as is.
interface StoredOAuth2 { accessToken?: string; accessTokenExpiration?: string | null; }
interface StoredUserSession { Token?: string; }
interface StoredAuthorization { data?: { oauth2?: StoredOAuth2 }; oauth2?: StoredOAuth2; }
interface StoredAuthConfig { token?: string; }

// The WinApp's WebView2 exposes window.chrome.webview; a browser and the mobile apps do not.
export function getWinAppWebview(win: unknown = typeof window === "undefined" ? undefined : window): WebView2Host | null {
    if (!win || typeof win !== "object" || !("chrome" in win)) return null;
    const chrome = win.chrome;
    if (!chrome || typeof chrome !== "object" || !("webview" in chrome)) return null;
    const webview = chrome.webview;
    if (!webview || typeof webview !== "object" || !("addEventListener" in webview) || typeof webview.addEventListener !== "function") return null;
    // Checked above: the WebView2 host has addEventListener.
    const host: WebView2Host = webview as WebView2Host;
    return host;
}

export function isWinAppWebview(win?: unknown): boolean {
    return getWinAppWebview(win) !== null;
}

function readPushedToken(data: unknown): PushedToken | null {
    if (!data || typeof data !== "object" || !("message" in data) || data.message !== WEBVIEW_ACCESS_TOKEN) return null;
    const payload = "data" in data ? data.data : null;
    if (!payload || typeof payload !== "object" || !("accessToken" in payload)) return null;
    if (typeof payload.accessToken !== "string" || !payload.accessToken) return null;
    const expiration = "accessTokenExpiration" in payload && typeof payload.accessTokenExpiration === "string"
        ? payload.accessTokenExpiration
        : null;
    return { accessToken: payload.accessToken, accessTokenExpiration: expiration };
}

function updateJson<T extends object>(storage: KeyValueStorage, key: string, update: (value: T) => void) {
    const value: T | null = JSON.parse(storage.getItem(key) || "null");
    if (!value) return;
    update(value);
    storage.setItem(key, JSON.stringify(value));
}

function storeAccessToken(storage: KeyValueStorage, token: PushedToken) {
    updateJson<StoredUserSession>(storage, LOCALSTORAGE_USERSSION, session => { session.Token = token.accessToken; });
    updateJson<StoredAuthorization>(storage, LOCALSTORAGE_AUTH2, authorization => {
        for (const oauth2 of [authorization.data?.oauth2, authorization.oauth2]) {
            if (!oauth2) continue;
            oauth2.accessToken = token.accessToken;
            oauth2.accessTokenExpiration = token.accessTokenExpiration;
        }
    });
    updateJson<StoredAuthConfig>(storage, LOCALSTORAGE_AUTH, auth => { auth.token = token.accessToken; });
}

export function listenForWebviewAccessToken(host: WebView2Host, storage: KeyValueStorage, onToken: () => void): void {
    host.addEventListener("message", event => {
        const token = readPushedToken(event.data);
        if (!token) return;
        storeAccessToken(storage, token);
        onToken();
    });
}
